import { API_CONFIG } from '@/config/api.config';
import { authService } from '@/auth';

export interface ApiRequestOptions extends RequestInit {
  timeout?: number;
}

export interface ApiError extends Error {
  status?: number;
  body?: unknown;
}

export abstract class BaseApiClient {
  protected abstract baseUrl: string;

  private timeout: number;

  constructor() {
    this.timeout = API_CONFIG.TIMEOUT;
  }

  protected async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const controller = new AbortController();
    const timeoutValue = options.timeout || this.timeout;
    console.log(`[BaseApiClient] Endpoint: ${endpoint}, Timeout: ${timeoutValue}ms`);
    const timeoutId = setTimeout(() => {
      console.warn(`[BaseApiClient] Request TO TIMED OUT: ${endpoint} after ${timeoutValue}ms`);
      controller.abort();
    }, timeoutValue);

    try {
      const token = authService.getAccessToken();
      const url = `${this.baseUrl}${endpoint}`;

      // Don't set Content-Type for FormData - browser will set it automatically with boundary
      const isFormData = options.body instanceof FormData;
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (!isFormData) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      clearTimeout(timeoutId);

      if (response.status === 401) {
        const refreshed = await authService.refreshAccessToken();
        if (refreshed) {
          return this.request<T>(endpoint, options);
        }
        await authService.logout();
        throw this.createApiError('Unauthorized', 401);
      }

      if (!response.ok) {
        const errorBody = await response.text().catch(() => null);
        throw this.createApiError(
          `HTTP error! status: ${response.status}`,
          response.status,
          errorBody
        );
      }

      if (response.status === 204) {
        return undefined as T;
      }

      const method = options.method || 'GET';
      console.log(`API Request: ${method} ${url}`, { options, responseStatus: response.status });

      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const json = await response.json();
        console.log(`API Response JSON:`, json);
        return json;
      }

      const text = await response.text();
      console.log(`API Response Text:`, text);
      return text as T;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw this.createApiError('Request timeout', 408);
      }

      throw error;
    }
  }

  private createApiError(message: string, status?: number, body?: unknown): ApiError {
    const error = new Error(message) as ApiError;
    error.status = status;
    error.body = body;
    return error;
  }

  get<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, data: unknown, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  put<T>(endpoint: string, data: unknown, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch<T>(endpoint: string, data: unknown, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  upload<T>(endpoint: string, formData: FormData, options: ApiRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: formData,
    });
  }
}
